/**
 * SecuGen WebAPI integration for Hamster Plus (HU20) USB fingerprint scanner.
 *
 * Requires the SgiBioSrv client application running on the admin's PC.
 * Download from: https://webapi.secugen.com/
 *
 * The local service exposes REST endpoints at https://localhost:8443
 */

const SECUGEN_URLS = [
  "https://localhost:8443",
  "http://localhost:8000",
];

export interface SGIFPCaptureResponse {
  ErrorCode: number;
  BMPBase64: string;        // Base64-encoded BMP image of the fingerprint
  TemplateBase64: string;   // Base64-encoded fingerprint template
  ImageHeight: number;
  ImageWidth: number;
  ImageDPI: number;
  ImageQuality: number;
  NFIQ: number;             // NIST Fingerprint Image Quality (1=best, 5=worst)
  SerialNumber: string;
  WSQImageSize?: number;
  WSQImage?: string;
}

/**
 * Capture a single fingerprint from the SecuGen scanner.
 * Returns the capture response including BMP image and template data.
 */
export async function captureFingerprint(opts?: {
  timeout?: number;
  quality?: number;
  baseUrl?: string;
}): Promise<SGIFPCaptureResponse> {
  const timeout = opts?.timeout ?? 10000;
  const quality = opts?.quality ?? 50;
  const baseUrl = opts?.baseUrl;

  const params = new URLSearchParams({
    Timeout: String(timeout),
    Quality: String(quality),
    templateFormat: "ISO",
    imageWSQ: "0",
  });

  // If a known-good baseUrl is provided, use it directly
  const urls = baseUrl ? [baseUrl] : SECUGEN_URLS;
  let lastError: Error | null = null;

  for (const url of urls) {
    try {
      const response = await fetch(`${url}/SGIFPCapture`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      if (!response.ok) {
        lastError = new Error(`SecuGen service returned HTTP ${response.status}`);
        continue;
      }

      const data: SGIFPCaptureResponse = await response.json();

      if (data.ErrorCode !== 0) {
        throw new Error(getSecuGenError(data.ErrorCode));
      }

      return data;
    } catch (err: any) {
      lastError = err;
    }
  }

  throw lastError ?? new Error("Could not connect to SecuGen service");
}

/**
 * Convert the BMPBase64 from SecuGen response to a PNG Blob for storage.
 */
export function bmpBase64ToBlob(bmpBase64: string): Blob {
  const byteCharacters = atob(bmpBase64);
  const byteNumbers = new Uint8Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([byteNumbers], { type: "image/bmp" });
}

/**
 * Convert BMP base64 to a PNG Blob via canvas for smaller file size.
 */
export async function bmpBase64ToPngBlob(bmpBase64: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas context unavailable"));
        return;
      }
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Failed to convert to PNG"));
        },
        "image/png"
      );
    };
    img.onerror = () => reject(new Error("Failed to load BMP image"));
    img.src = `data:image/bmp;base64,${bmpBase64}`;
  });
}


function getSecuGenError(code: number): string {
  const errors: Record<number, string> = {
    51: "System file load failure",
    52: "Sensor chip initialization failed",
    53: "Device not found",
    54: "Fingerprint image capture timeout",
    55: "No device available",
    56: "Driver load failed",
    57: "Wrong image",
    58: "Lack of bandwidth",
    59: "Device busy",
    60: "Cannot get serial number of the device",
    61: "Unsupported device",
    63: "SgiBioSrv didn't start; Try different port",
    69: "License error",
  };
  return errors[code] || `SecuGen error code: ${code}`;
}
